/**
 * "Atiende" — quien lleva a cada cliente, tambien cuando esa persona todavia no
 * tiene asiento en Deskte.
 *
 * Lissette, 2026-09-03: *"pero no puede ser un texto libre? es decir que traiga
 * el nombre que traiga asi no este el seat?"*. Importando la cartera de Apizeal
 * la columna trae nombres de personas reales que no estan de alta en el CRM. Si
 * solo se acepta un asiento, ese nombre se tira y hay que reescribirlo ficha por
 * ficha.
 *
 * POR QUE HACEN FALTA DOS SITIOS Y NO UNO. `attendedBySeatId` guarda un ID de
 * asiento, no un nombre: la pantalla lo traduce con las etiquetas de la agencia
 * y ADEMAS ese id se copia como responsable al abrir un ticket del cliente
 * (tickets.routes.ts). Un texto suelto ahi dejaria tickets asignados a un
 * usuario que no existe: no saldrian en "mis tickets" de nadie y no le llegaria
 * aviso a nadie. Por eso el nombre crudo vive en `attendedByName`, y solo cuando
 * no hubo asiento al que engancharlo.
 *
 * DE DONDE SALE CADA COSA. `seatLabelFor` y `attendOptions` vivian en
 * AttendedBy.jsx y `resolveSeatId` en MondayMigrationWizard.jsx. Se mudan aqui
 * TAL CUAL —mismo comportamiento, sin tocar una linea de su logica— para que el
 * importador de Excel use EL MISMO emparejador que ya lleva meses funcionando
 * con las exportaciones de monday, en vez de estrenar uno paralelo. Los dos
 * archivos de origen siguen exportandolos, asi que ningun import existente se
 * entera.
 *
 * Modulo sin React a proposito: la logica se prueba desde vitest
 * (tests/unit/atiende.test.ts) sin montar nada.
 */

export const COLOR_ASIENTO = '#00d4ff';
/** Gris apagado: dice "esto es un nombre, todavia no un asiento". */
export const COLOR_TEXTO_LIBRE = '#7aa3c0';

/** Sin acentos, sin mayusculas y sin puntuacion. Igual que el normKey de monday. */
export function normalizarNombre(s) {
  return String(s == null ? '' : s).trim().toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Miembros del equipo que pueden aparecer como "Atiende". */
export function attendOptions(users) {
  return (users || []).filter((u) => u && u.id && (u.name || u.displayName));
}

/**
 * { name, color } de un asiento: la etiqueta de la agencia manda, y si no la hay
 * se usa el nombre real. null para un id vacio.
 */
export function seatLabelFor(seatId, seatLabels, users) {
  if (!seatId) return null;
  const lbl = (seatLabels || {})[seatId] || {};
  const u = (users || []).find((x) => x.id === seatId);
  const name = (lbl.name && lbl.name.trim()) || (u ? (u.name || u.displayName || '') : '');
  return { name: name || 'Seat', color: lbl.color || COLOR_ASIENTO };
}

/** El equipo como [{ id, name }], que es lo que espera resolveSeatId. */
export function asientosDeLaAgencia(users, seatLabels) {
  return attendOptions(users).map((u) => ({
    id: u.id,
    name: (seatLabelFor(u.id, seatLabels, users) || {}).name || u.name || u.displayName || '',
  }));
}

/**
 * Un nombre suelto -> el id del asiento que le corresponde, o '' si ninguno.
 *
 * Cuatro intentos, de mas exacto a mas tolerante, y ninguno adivina cuando
 * quedan dos candidatos. Viene entero de MondayMigrationWizard.jsx.
 */
export function resolveSeatId(handleText, seats) {
  const h = normalizarNombre(handleText);
  if (!h || !seats?.length) return '';
  // 1) exact normalized name match
  let hit = seats.find((s) => normalizarNombre(s.name) === h);
  if (hit) return hit.id;
  // 2) one contains the other (e.g. "Asniel" ↔ "Asniel Lopez Silva")
  hit = seats.find((s) => { const n = normalizarNombre(s.name); return n && (n.includes(h) || h.includes(n)); });
  if (hit) return hit.id;
  // 3) letras dobladas. "Yessica Aguire" contra el seat "Yesica Aguirre"
  // (Lissette 2026-08-14, exportacion de Allinsure Group): doblar o comerse una
  // letra repetida es EL error de tecleo de los nombres españoles — Yesica /
  // Yessica, Aguirre / Aguire, Ana / Anna. Colapsando las repeticiones los dos
  // se vuelven la misma cadena.
  //
  // Solo vale si deja UN candidato. Si colapsar acerca a dos seats distintos no
  // se elige ninguno: dejar el handle sin asignar es un hueco que se ve y se
  // arregla; asignarle la cartera a la persona equivocada, no.
  const squash = (x) => normalizarNombre(x).replace(/[^a-z0-9 ]/g, '').replace(/(.)\1+/g, '$1');
  const hSq = squash(h);
  if (hSq) {
    const near = seats.filter((s) => squash(s.name) === hSq);
    if (near.length === 1) return near[0].id;
  }
  // 4) first-token match (first name), tambien tolerando letras dobladas
  const hTok = h.split(' ')[0];
  hit = seats.find((s) => normalizarNombre(s.name).split(' ')[0] === hTok);
  if (hit) return hit.id;
  const hTokSq = squash(hTok);
  if (hTokSq) {
    const near = seats.filter((s) => squash(normalizarNombre(s.name).split(' ')[0]) === hTokSq);
    if (near.length === 1) return near[0].id;
  }
  return '';
}

/**
 * Lo que se guarda en el cliente a partir del nombre que trae el archivo.
 * Uno de los dos, nunca los dos: si hubo asiento manda el id y el texto sobra.
 */
export function atiendeDesdeElArchivo(nombre, seats) {
  const texto = String(nombre == null ? '' : nombre).trim().slice(0, 160);
  if (!texto) return { attendedBySeatId: '', attendedByName: '' };
  const id = resolveSeatId(texto, seats);
  return id ? { attendedBySeatId: id, attendedByName: '' } : { attendedBySeatId: '', attendedByName: texto };
}

/**
 * Lo que pinta el chip: nombre, color y si es un asiento de verdad o solo un
 * nombre. Devuelve null cuando no hay ni lo uno ni lo otro.
 */
export function etiquetaDeAtiende(seatId, nombreLibre, seatLabels, users) {
  if (seatId) {
    const l = seatLabelFor(seatId, seatLabels, users);
    return l ? { ...l, libre: false } : null;
  }
  const texto = String(nombreLibre == null ? '' : nombreLibre).trim();
  if (!texto) return null;
  return { name: texto, color: COLOR_TEXTO_LIBRE, libre: true };
}

/** Prefijo del filtro de la lista para un nombre sin asiento. */
export const PREFIJO_LIBRE = 'txt:';

/** Los nombres sueltos que hay hoy en la cartera, ordenados y sin repetir. */
export function nombresLibresDeLaCartera(clients) {
  const vistos = new Map();
  for (const c of (clients || [])) {
    if (!c || c.attendedBySeatId) continue;
    const n = String(c.attendedByName == null ? '' : c.attendedByName).trim();
    if (!n) continue;
    const k = normalizarNombre(n);
    if (!vistos.has(k)) vistos.set(k, n);
  }
  return [...vistos.values()].sort((a, b) => a.localeCompare(b, 'es'));
}

/** ¿Este cliente entra en el filtro "Atiende" elegido? */
export function pasaElFiltroDeAtiende(cliente, filtro) {
  const c = cliente || {};
  if (!filtro || filtro === 'all') return true;
  if (filtro === 'none') return !c.attendedBySeatId && !String(c.attendedByName == null ? '' : c.attendedByName).trim();
  if (filtro.startsWith(PREFIJO_LIBRE)) {
    if (c.attendedBySeatId) return false;
    return normalizarNombre(c.attendedByName) === normalizarNombre(filtro.slice(PREFIJO_LIBRE.length));
  }
  return c.attendedBySeatId === filtro;
}

// ── LAS OPCIONES DEL FILTRO EN UN REPORTE ──────────────────────────────────
//
// El filtro de la lista de clientes (arriba) se arma con la cartera que la
// pantalla ya tiene cargada. El constructor de reportes NO la tiene, asi que
// las opciones se las da el servidor (`/clients/filter-options`), con cuantas
// fichas hay detras de cada una. Vive aqui, junto al vocabulario que usa, y no
// dentro del componente: asi se puede probar sin montar React.

/**
 * LAS OPCIONES DEL CAMPO "ATIENDE", a partir de lo que hay en la cartera.
 *
 * Un valor es el ID del asiento, o el nombre suelto con el prefijo `txt:` — EL
 * MISMO vocabulario que `pasaElFiltroDeAtiende` de aquí arriba, y el que
 * entiende el servidor (`deskte-api/src/utils/atiende.ts`). Guardar el id y no
 * el nombre es lo que hace que un reporte guardado siga funcionando cuando la
 * agencia le cambie la etiqueta a alguien.
 *
 * El NUMERO va dentro de la etiqueta a propósito. La misma persona puede
 * aparecer dos veces —como asiento y como nombre importado— y sin los números
 * delante, filtrar por el asiento devuelve doce fichas de trescientas y parece
 * que el filtro está roto. Con ellos se ve lo que pasa: hay 288 fichas con su
 * nombre escrito que todavía no están enganchadas a su asiento.
 */
export function opcionesDeAtiendeParaReporte(atiende, es) {
  const asientos = Array.isArray(atiende?.asientos) ? atiende.asientos : [];
  const sueltos = Array.isArray(atiende?.nombres) ? atiende.nombres : [];
  const options = [];
  const labels = {};
  for (const a of asientos) {
    if (!a || !a.id) continue;
    options.push(a.id);
    labels[a.id] = `${a.nombre || a.id} (${a.n})`;
  }
  for (const n of sueltos) {
    const nombre = String(n?.nombre || '').trim();
    if (!nombre) continue;
    const v = `${PREFIJO_LIBRE}${nombre}`;
    options.push(v);
    // "sin asiento" no es un adorno: dice por qué esa persona sale dos veces y
    // cuál de las dos entradas hay que reparar.
    labels[v] = `${nombre} · ${es ? 'sin asiento' : 'no seat'} (${n.n})`;
  }
  return { options, labels };
}

/**
 * EL ENCARGADO DE UNA RENOVACIÓN, con el de la ficha como punto de partida.
 *
 * Lissette, 2026-09-30: *"en Renovaciones, en Encargado no se trae el que ya
 * tiene asignado; que se traiga de una vez el que tiene asignado y también dé
 * la opción de cambiarlo"*.
 *
 * - Si la renovación ya tiene su encargado guardado (`customData.encargado`),
 *   manda ese — también cuando se guardó VACÍO a propósito ("Sin asignar"):
 *   quitarlo es una decisión, y no puede volver a aparecer solo.
 * - Si nunca se tocó, sale el asiento que atiende al cliente en su ficha. Un
 *   nombre suelto sin asiento no vale: el selector solo sabe de asientos.
 */
export function encargadoDeLaRenovacion(poliza, cliente) {
  const cd = poliza && poliza.customData;
  if (cd && typeof cd === 'object' && Object.prototype.hasOwnProperty.call(cd, 'encargado')) {
    return String(cd.encargado || '');
  }
  return String((cliente && cliente.attendedBySeatId) || '');
}
